import React, { useCallback, useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'mui-modal-provider';

import { yup } from '../../../../../services/yup';
import { useBind } from '../../../../../hooks/use-bind';
import { UPDATE_DEPENDENT } from '../../../../../graphql/profile/mutations';
import { useActionsInProgress } from '../../../../../graphql/preloader/actions/actions-in-progress';
import { useCancellablePromise } from '../../../../../hooks/use-cancellable-promise';
import { ProfileForm } from './profile-form';
import { KidsProfileFormData } from './profile-form/profile-form.models';
import { Page } from '../../page/page';
import { InternalLinks, SECONDARY_BACKGROUND } from '../../../../../constants';
import { ContentWrapper } from '../../../../common/content-wrapper';
import { Box } from '../../../../common/box';
import {
  useFirstNameCyrillicValidation,
} from '../../../../../hooks/use-first-name-cyrillic-validation';
import {
  useLastNameCyrillicValidation,
} from '../../../../../hooks/use-last-name-cyrillic-validation';
import { TechIssues } from '../../../../common/tech-issues';
import { Preloader } from '../../../../common/preloader';
import { DependentProfile } from '../../../../../graphql/profile/models/kids.models';
import { GET_DEPENDENT_BY_ID } from '../../../../../graphql/profile/queries';
import { ErrorModal } from '../error-modal';
import { v2025PP16783 } from '../../../../../constants/veeva';
import { useStyles } from './view-kids-profile.styles';

export const ViewKidsProfile = (): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const dependentId = window?.location?.search?.slice(4) || '';
  const {
    data: kidsData, loading: kidsLoading, error: kidsError,
  } = useQuery(GET_DEPENDENT_BY_ID, { fetchPolicy: 'network-only', variables: { id: dependentId } });

  const [updateDependent, updateDependentMutationSettings] = useMutation(UPDATE_DEPENDENT);
  const { loading } = updateDependentMutationSettings;
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();
  const {
    makeCancellablePromise,
    CancelledPromiseOnUnmountError,
  } = useCancellablePromise();

  const firstNameValidation = useFirstNameCyrillicValidation();
  const lastNameValidation = useLastNameCyrillicValidation();

  const validationSchema = useMemo(
    () => yup.object({ firstName: firstNameValidation, lastName: lastNameValidation }),
    [firstNameValidation, lastNameValidation],
  );

  const loadingBind = useBind(loading);
  const makeCancellablePromiseBind = useBind(makeCancellablePromise);
  const CancelledPromiseOnUnmountErrorBind = useBind(CancelledPromiseOnUnmountError);
  const updateDependentBind = useBind(updateDependent);
  const addActionInProgressBind = useBind(addActionInProgress);
  const removeActionInProgressBind = useBind(removeActionInProgress);
  const navigate = useNavigate();
  const { showModal } = useModal();

  const handleError = useCallback(() => {
    showModal(ErrorModal, {
      title: 'viewKidsProfile.error.title',
      text: 'createKidsProfile.error.text',
      firstButton: 'createKidsProfile.error.button',
    });
  }, [showModal]);

  const handleUpdateDependent = useCallback(async (data: KidsProfileFormData) => {
    if (loadingBind.current || !data) {
      return;
    }

    addActionInProgressBind.current();

    try {
      const firstName = data?.firstName?.trim();
      const lastName = data?.lastName?.trim();

      await makeCancellablePromiseBind.current(
        updateDependentBind.current({
          variables: {
            dependent: { id: dependentId, firstName, lastName },
          },
        }),
      );

      removeActionInProgressBind.current();
      setSaveSuccessful(true);

      setTimeout(() => {
        navigate(InternalLinks.forKids);
      }, 0);
    } catch (error: any) {
      removeActionInProgressBind.current();

      if (error instanceof CancelledPromiseOnUnmountErrorBind.current) {
        return;
      }

      handleError();
    }
  }, [
    loadingBind,
    makeCancellablePromiseBind,
    CancelledPromiseOnUnmountErrorBind,
    updateDependentBind,
    addActionInProgressBind,
    removeActionInProgressBind,
    dependentId,
    navigate,
    handleError,
  ]);

  return (
    <>
      {!!kidsError && <TechIssues />}
      {kidsLoading && <Preloader />}
      {!!kidsData && (
        <Page
          title={intl.formatMessage({ id: 'createKidsProfile.pageTitle' })}
          style={`body {background-color: ${SECONDARY_BACKGROUND};}`}
        >
          <ContentWrapper withoutPaddings>
            <Box title={<FormattedMessage id="createKidsProfile.title" />} isBackButton>
              <div className={classes.veevaNumber}>{v2025PP16783}</div>
              {!!kidsData?.getDependentById && (
                <ProfileForm
                  onSubmit={handleUpdateDependent}
                  validationSchema={validationSchema}
                  profile={kidsData.getDependentById as DependentProfile}
                  disableLeavePrompt={saveSuccessful}
                />
              )}
            </Box>
          </ContentWrapper>
        </Page>
      )}
    </>
  );
};
